/**
 * What one kiosk screen tells the next one about the trip between them.
 *
 * Every navigation inside the kiosk is a full page load (`window.location.href`
 * rather than `$router.push`, so no state from a previous guest survives — 8.
 * Privacy), which means the screen that lands has no way of knowing what the
 * guest did to get there. The start screen in particular has to tell its own
 * boot — the kiosk waking up, which is what "Now Starting" announces — from a
 * guest arriving back on it, which announces nothing.
 *
 * This marker draws that line. Everything that crosses a kiosk navigation
 * goes through here, is read once and consumed, and expires on its own when it
 * is never collected (the trip never lands) — so nothing left behind by one
 * guest is ever honored by an unrelated page load later on.
 *
 * Two things cross today:
 *
 *   - the internal-return marker above, which holds no guest data;
 *   - the QR code the start screen just scanned, on its way to the hub that
 *     looks it up. That one IS guest data (the pre-check-in mail's
 *     email|pass), which is why it rides in session storage rather than in
 *     the URL: a query string would leave it in the tablet's history for the
 *     next guest to find (8. Privacy).
 */

// The guest is coming back to a screen they have already seen — Back, the idle
// auto-reset, the end of a session. Nothing is starting, so nothing announces
// a start.
const INTERNAL_RETURN_KEY = 'kiosk_internal_return';

// The QR code the start screen scanned for the Confirm Pre Check-in flow. The
// hub that loads next resolves it as if the guest had scanned it there.
const SCANNED_QR_KEY = 'kiosk_scanned_qr';

// Longer than any navigation between two kiosk screens, short enough that a
// marker left behind by a failed one is never honored by a later, unrelated
// page load on a tablet that runs unattended for days.
const MAX_AGE_MS = 30000;

// Storage can throw (private browsing, storage disabled by policy). A kiosk
// that cannot write a marker simply falls back to the quieter announcement,
// which is the safe direction to fail in.
function mark(key) {
    try {
        window.sessionStorage.setItem(key, String(Date.now()));
    } catch (e) {
        // Nothing to carry over; the next screen loads without the hint.
    }
}

function take(key) {
    try {
        const raw = window.sessionStorage.getItem(key);
        if (raw === null) return false;

        window.sessionStorage.removeItem(key);
        return Date.now() - Number(raw) < MAX_AGE_MS;
    } catch (e) {
        return false;
    }
}

// Same take-once, same expiry, for a marker that carries a value with it.
function stash(key, value) {
    try {
        window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value }));
    } catch (e) {
        // Nothing carried over; the next screen simply asks the guest again.
    }
}

function takeValue(key) {
    try {
        const raw = window.sessionStorage.getItem(key);
        if (raw === null) return null;

        window.sessionStorage.removeItem(key);
        const { at, value } = JSON.parse(raw);
        return Date.now() - Number(at) < MAX_AGE_MS ? value : null;
    } catch (e) {
        return null;
    }
}

export function markKioskInternalReturn() {
    mark(INTERNAL_RETURN_KEY);
}

export function takeKioskInternalReturn() {
    return take(INTERNAL_RETURN_KEY);
}

export function stashKioskScannedQr(value) {
    stash(SCANNED_QR_KEY, String(value ?? ''));
}

// The scanned code, or null when there is none (or it has expired).
export function takeKioskScannedQr() {
    const value = takeValue(SCANNED_QR_KEY);
    return value ? String(value) : null;
}
